import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

/** Milliseconds of quiet typing before the server is asked. */
export const UNIQUENESS_DEBOUNCE_MS = 350;

/** The error key a taken value is reported under. */
export const TAKEN_ERROR = 'taken';

export interface UniqueValueOptions {
  /** Asks the server. Resolves true when the value is free to use. */
  check: (value: string) => Observable<boolean>;
  /**
   * The value this record already holds, when one is being edited.
   *
   * Read through a function rather than captured, because the form is built before
   * the record it is editing has loaded.
   */
  currentValue?: () => unknown;
  /**
   * How two values are compared for "unchanged". Lets an email ignore letter case
   * without the comparison having to know it is looking at an email.
   */
  normalize?: (value: string) => string;
  debounceMs?: number;
}

const asString = (value: unknown): string =>
  value === null || value === undefined ? '' : `${value}`;

/**
 * Marks a field whose value is already in use, asked of the server as the user
 * types.
 *
 * Three things it deliberately does NOT do.
 *
 * It does not ask about a value the record already holds. Clearing the field and
 * typing the same thing back in is the most ordinary thing to do on an edit form,
 * and it has to cost nothing and complain about nothing. The server is told to
 * exclude the record too, so this is a shortcut and not the guarantee — but it is
 * the shortcut that makes the common edit silent.
 *
 * It does not ask about an empty field. That is the required-field validator's
 * business, and an empty value collides with nothing.
 *
 * And it does not fail the field when the request fails. A field that reddens
 * because the network dropped would be unfixable from inside the form, and the
 * value is checked again on save by the same comparison — so an unanswerable
 * question is left unanswered rather than answered wrongly.
 *
 * The debounce is a `timer` inside the validator rather than a debounced stream
 * outside it: Angular drops the previous run the moment the value changes again,
 * so the timer of every superseded keystroke is discarded before it can fire, and
 * only the value the user stopped on is ever sent.
 */
export function uniqueValueValidator(
  options: UniqueValueOptions,
): AsyncValidatorFn {
  const normalize = options.normalize ?? ((value: string) => value);
  const debounceMs = options.debounceMs ?? UNIQUENESS_DEBOUNCE_MS;

  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const value = asString(control.value);
    if (!value.trim()) {
      return of(null);
    }

    const current = asString(options.currentValue?.());
    if (current.trim() && normalize(value.trim()) === normalize(current.trim())) {
      return of(null);
    }

    return timer(debounceMs).pipe(
      // Sent exactly as typed. The server compares the value the save will store,
      // and trimming here would ask about a different one — green field, refused
      // save.
      switchMap(() => options.check(value)),
      map((available) => (available ? null : { [TAKEN_ERROR]: true })),
      catchError(() => of(null)),
      first(),
    );
  };
}
